/**
 * Single fetch wrapper. All API calls go through here.
 * Throws `ApiError` on non-2xx so callers can `try/catch` cleanly.
 */
export class ApiError extends Error {
  constructor(message, { status, code, details }) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

const BASE = '/api/v1';

// A 401 here is an expected outcome (bad login, or the initial not-logged-in
// session check), not a session expiring mid-use — don't redirect for these.
const SESSION_CHECK_EXEMPT = new Set(['/auth/login', '/auth/me']);

const request = async (method, path, body) => {
  const res = await fetch(`${BASE}${path}`, {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401 && !SESSION_CHECK_EXEMPT.has(path)) {
    window.location.href = '/login';
    throw new ApiError('Session expired', { status: 401, code: 'UNAUTHORIZED' });
  }

  if (res.status === 204) return null;

  const json = await res.json().catch(() => null);
  if (!res.ok || !json?.ok) {
    const err = json?.error ?? {};
    throw new ApiError(err.message ?? res.statusText, {
      status: res.status,
      code: err.code,
      details: err.details,
    });
  }
  return json.data;
};

export const api = {
  get: (path) => request('GET', path),
  post: (path, body) => request('POST', path, body),
  patch: (path, body) => request('PATCH', path, body),
  put: (path, body) => request('PUT', path, body),
  delete: (path) => request('DELETE', path),
};
